import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';

interface VisitorStats {
    active: number;
    today: number;
    total: number;
    recentPages: string[];
}

@Component({
    selector: 'em-visitors-widget',
    standalone: true,
    imports: [CommonModule, HttpClientModule],
    template: `
        <div class="em-widget">
            <div class="em-widget-header">
                <div class="em-widget-title">
                    <span class="em-icon">📊</span>
                    Visitas en Tiempo Real
                </div>
                <div class="em-live-indicator" [ngClass]="{'active': isActive}">
                    <span class="em-dot"></span>
                    {{ isActive ? 'EN VIVO' : 'SIN VISITAS' }}
                </div>
            </div>

            <div class="em-stats-grid">
                <div class="em-stat-card em-stat-active">
                    <div class="em-stat-number">{{ activeCount }}</div>
                    <div class="em-stat-label">Visitantes Activos</div>
                    <div class="em-stat-sublabel">últimos 5 min</div>
                </div>
                <div class="em-stat-card em-stat-today">
                    <div class="em-stat-number">{{ todayCount }}</div>
                    <div class="em-stat-label">Visitas Hoy</div>
                    <div class="em-stat-sublabel">sesiones únicas</div>
                </div>
                <div class="em-stat-card em-stat-total">
                    <div class="em-stat-number">{{ totalFormatted }}</div>
                    <div class="em-stat-label">Total Histórico</div>
                    <div class="em-stat-sublabel">desde siempre</div>
                </div>
            </div>

            <div class="em-widget-footer">
                Actualizado cada 10s · {{ lastUpdate }}
            </div>
        </div>
    `,
    styles: [`
        :host {
            display: block;
        }
        .em-widget {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 24px;
            color: #1e293b;
            font-family: inherit;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .em-widget-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }

        .em-widget-title {
            font-size: 16px;
            font-weight: 600;
            color: #334155;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .em-icon { font-size: 20px; }

        .em-live-indicator {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 1px;
            color: #94a3b8;
            text-transform: uppercase;
        }

        .em-live-indicator.active { color: #10b981; }

        .em-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #cbd5e1;
            display: inline-block;
        }

        .em-live-indicator.active .em-dot {
            background: #10b981;
            box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
            animation: emPulse 2s ease-in-out infinite;
        }

        @keyframes emPulse {
            0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(16, 185, 129, 0.4); }
            50% { opacity: 0.6; box-shadow: 0 0 16px rgba(16, 185, 129, 0.6); }
        }

        .em-stats-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
            margin-bottom: 16px;
        }

        .em-stat-card {
            background: #f8fafc;
            border: 1px solid #f1f5f9;
            border-radius: 8px;
            padding: 16px 12px;
            text-align: center;
            transition: all 0.2s ease;
        }

        .em-stat-card:hover {
            border-color: #cbd5e1;
            background: #f1f5f9;
        }

        .em-stat-active .em-stat-number { color: #10b981; }
        .em-stat-today .em-stat-number { color: #0ea5e9; }
        .em-stat-total .em-stat-number { color: #64748b; }

        .em-stat-number {
            font-size: 32px;
            font-weight: 700;
            line-height: 1;
            margin-bottom: 6px;
        }

        .em-stat-label {
            font-size: 12px;
            font-weight: 600;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .em-stat-sublabel {
            font-size: 10px;
            color: #94a3b8;
            margin-top: 2px;
        }

        .em-widget-footer {
            text-align: center;
            font-size: 10px;
            color: #94a3b8;
            padding-top: 8px;
            border-top: 1px solid #f1f5f9;
        }
    `]
})
export class VisitorsWidgetComponent implements OnInit, OnDestroy {
    activeCount = 0;
    todayCount = 0;
    totalFormatted = '0';
    isActive = false;
    lastUpdate = '';
    private intervalId: any;

    constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

    ngOnInit() {
        this.fetchStats();
        this.intervalId = setInterval(() => this.fetchStats(), 10000);
    }

    ngOnDestroy() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    fetchStats() {
        const baseUrl = window.location.origin;
        this.http.get<VisitorStats>(`${baseUrl}/api/pulse-stats?t=${Date.now()}`).subscribe({
            next: (data) => {
                this.activeCount = data.active || 0;
                this.todayCount = data.today || 0;
                this.isActive = this.activeCount > 0;
                const total = data.total || 0;
                this.totalFormatted = total >= 1000 ? (total / 1000).toFixed(1) + 'K' : total.toString();
                this.lastUpdate = new Date().toLocaleTimeString('es-CL');
                this.cdr.detectChanges();
            },
            error: () => {
                this.lastUpdate = 'Error al cargar';
                this.cdr.detectChanges();
            }
        });
    }
}
